<template>
  <div class="room-media-share-component" data-grm-comp="RoomMediaShare">
    <div class="room-media-share">
      <div class="room-media-share-header">
        <div class="room-media-share-info">
          <button class="room-media-share-button menu" @click="onToggleMediaList"></button>
          <p class="room-media-share-title" v-if="mediaFile">{{ mediaFile.filename }}</p>
        </div>
      </div>
      <div class="room-media-share-body">
        <div class="room-media-share-none-file" v-if="!sortedRoomMediaFile(roomMedia).length">
          <p class="room-media-share-none-file-text title">{{ $t("media.share.noFile") }}</p>
          <p class="room-media-share-none-file-text description">{{ $t("media.share.upload.text") }}</p>
          <p class="room-media-share-none-file-text description">[{{ $t("media.list.forms") }}]</p>
          <p class="room-media-share-none-file-text description">mp3, mp4(H.264), WebM(VP8, VP9), ogg</p>
          <GrmBtn class="mt-3"
                  v-if="hasEmceeRole()"
                  @click="onUploadFile">{{ $t("media.share.upload.btn") }}</GrmBtn>
        </div>
        <div class="room-media-share-video-wrapper" v-show="isMediaVideo">
          <video webkit-playsinline playsinline videoplay
                 ref="mediaVideo"
                 @error="eventMedia"
                 @durationchange="eventMedia"
                 @resize="eventMedia"
                 @canplay="eventMedia"
                 @play="eventMedia"
                 @playing="eventMedia"
                 @pause="eventMedia"
                 @seeked="eventMedia"
                 @ended="eventMedia"
                 class="room-media-share-video" />
        </div>
        <div class="room-media-share-video-wrapper" v-show="isMediaAudio">
          <audio webkit-playsinline
                 ref="mediaAudio"
                 @error="eventMedia"
                 @durationchange="eventMedia"
                 @resize="eventMedia"
                 @canplay="eventMedia"
                 @play="eventMedia"
                 @playing="eventMedia"
                 @pause="eventMedia"
                 @seeked="eventMedia"
                 @ended="eventMedia"
                 class="room-media-share-audio" />
        </div>
      </div>
      <div v-if="!!roomMedia.mediaId"
           class="room-media-share-bottom"
      >
        <div class="room-media-share-controls">
          <button class="room-media-share-control-button"
                  :class="[roomMedia.playState === 'play' ? 'pause' : 'play']" @click="changeMediaPlayState">

          </button>
          <div class="room-media-share-control-range"
               :class="{ 'pl-3': !hasEmceeRole() }"
          >
            <GrmSlider :value="mediaTimeRange"
                       :max="rangeMax"
                       @input="updateTempSeekTime"
                       @touchstart="isStartChangeMediaTime = true"
                       @touchend="changeTempSeekTime"
                       @pointerdown="isStartChangeMediaTime = true"
                       @pointerup="changeTempSeekTime"
                       @mousedown="isStartChangeMediaTime = true"
                       @mouseup="changeTempSeekTime"
            />
          </div>
          <div class="room-media-share-control-time-container">
            <p class="room-media-share-control-time current">{{ mediaPlayTime }}</p>
            <p class="room-media-share-control-time duration">{{ mediaPlayDuration }}</p>
          </div>
          <button class="room-media-share-control-button speaker" :class="[isMuted ? 'off' : 'on']" @click="toggleVolume"></button>
          <div v-if="!Device.isIOSSafari()" class="room-media-share-control-range volume">
            <GrmSlider :value="currentPlayer?.volume" :max="1" :step="0.1" @input="changeVolume"/>
          </div>
        </div>
      </div>
      <RoomMediaList
        :class="{ show: isShowMediaList }"
        @onToggleMediaList="onToggleMediaList"
        @reqUploadMediaFile="onUploadFile"
        :roomGroupId="roomGroupId"
      />
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations} from "vuex";
import _, {throttle} from "lodash";

import {Room} from "@/core/api/room.api";
import {Device} from "@/utils";

import GrmBtn from "@/components/Common/button/GrmBtn";
import GrmSlider from '@/components/Common/slider/GrmSlider';
import RoomMediaList from '@/components/Room/Media/List';

export default {
  name: 'RoomMediaShare',
  props: {
    roomGroupId: {
      type: String,
      default: 'default'
    }
  },
  components: {
    GrmBtn,
    GrmSlider,
    RoomMediaList,
  },
  data() {
    return {
      Device,
      isShowMediaList: true,
      rangeMax: 1000,
      showMediaControls: true,
      mediaPlayTime:'00:00',
      mediaPlayDuration:'/ 00:00',
      mediaTimeRange:0,
      isMuted: false,
      playTimeTimer: null,
      stopTimer: true,
      currentMediaId: null,
      reload: false,
      showAlert: true,
      isStartChangeMediaTime: false,
      tempSeekTime: 0
    }
  },
  created() {
    this.$nextTick()
        .then(this.reloadCurrentPlayer())

    this._events = _.clone(_Events);
    this.$roomApi.addEventListeners(this._events, this)
  },
  destroyed() {
    this.$roomApi.removeEventListeners(this._events)
  },
  mounted() {
    if(this.currentPlayer == undefined) {
      this.reloadCurrentPlayer()
    }
    let THIS = this;
    const initPlay = () => {
      if(!THIS.isInit && THIS.roomMedia && THIS.roomMedia.playState === 'play' && THIS.roomMedia.lastPlayDate) {
        let addTime = (Date.now() - Room.getJoinTime()) / 1000;
        let seekTime = parseInt(( new Date(Room.getEnterServerTime()) - new Date(THIS.roomMedia.lastPlayDate) ) / 1000 ) + parseInt(THIS.roomMedia.currentPlayTime) + addTime
        THIS.play(seekTime)
      }
    }
    this.currentPlayer && this.currentPlayer.addEventListener('canplay', () => {
      initPlay()
    }, {once: true})
    initPlay()
  },
  watch: {
    volume(val) {
      this.isMuted = val == 0
    }
  },
  computed: {
    ...mapGetters('media', [
      'getRoomMedia',
      'getRoomMediaFile',
      'getMediaFile',
      'isAudio',
      'isVideo',
      'isInit',
      'getNextRoomMediaFile',
      'sortedRoomMediaFile',
      'allowedFileExts'
    ]),
    ...mapGetters('user', [
      'me',
      'isMe',
      'hasEmceeRole',
      'roomUserList',
      'roomUserListOrderByEnterDateDesc'
    ]),

    roomMedia: function () {
      return this.getRoomMedia(this.roomGroupId)
    },
    roomMediaFile: function () {
      if(!this.roomMedia) return;
      return this.getRoomMediaFile(this.roomMedia)
    },
    mediaFile: function () {
      if(!this.roomMediaFile) return;
      return this.roomMediaFile.mediaFile
    },
    isMediaAudio: function() {
      if(!this.mediaFile) return;
      return this.isAudio(this.mediaFile)
    },
    isMediaVideo: function() {
      if(!this.mediaFile) return;
      return this.isVideo(this.mediaFile)
    },
    currentPlayer: function() {
      if(this.reload) {
        return this.isMediaAudio ? this.$refs.mediaAudio : this.$refs.mediaVideo;
      } else {
        return this.isMediaAudio ? this.$refs.mediaAudio : this.$refs.mediaVideo;
      }
    },
    notCurrentPlayer: function() {
      if(this.reload) {
        return !this.isMediaAudio ? this.$refs.mediaAudio : this.$refs.mediaVideo;
      } else {
        return !this.isMediaAudio ? this.$refs.mediaAudio : this.$refs.mediaVideo;
      }
    },
  },
  methods: {
    ...mapActions('media', [
      'playRoomMedia',
      'pauseRoomMedia',
      'changeRoomMedia',
      'uploadRoomMediaFile'
    ]),
    ...mapMutations('media', [
      'PLAY_ROOM_MEDIA',
      'PAUSE_ROOM_MEDIA'
    ]),
    onToggleMediaList() {
      this.isShowMediaList = !this.isShowMediaList
    },
    async onUploadFile(e) {
      if (!this.hasEmceeRole()) {
        this.$alert(this.$t('media.list.noRole'));
        return;
      }
      const files = await this.getFileChooser({
          multiple: false,
          accept: this.allowedFileExts,
          size: 1
      });
      for(const file of files){
          if(!this.validExtension(file)) {
              this.$alert(this.$t('media.error.validationFail'));
              continue;
          }

          try {
              await this.uploadRoomMediaFile({file, filename: file.name});
          } catch (error) {
              this.uploadRoomMediaFileError(error)
          }
      }
    },
    uploadRoomMediaFileError(error) {
      let errorMessage = ''
      switch (error.message) {
        case 'load_duration_fail':
          errorMessage = this.$t('media.error.durationFail')
          break;
        case 'video_codec_not_supported':
          errorMessage = this.$t('media.error.notSupportedCodec')
          break;
        case 'load_file_fail':
          errorMessage = this.$t('media.error.notLoadedFile')
          break;
        default:
          errorMessage = this.$t('media.error.uploadFail')
          break;
      }

      this.$alert(errorMessage);
    },
    validExtension (file){
      const fileNameArr = file.name.split('.')
      const extension = fileNameArr[fileNameArr.length - 1].toLowerCase()
      return extension === 'mp3' || extension === 'mp4' || extension === 'webm' || extension === 'ogg'
    },
    async setRoomMediaDownloadUrl() {
      if(!this.roomMedia.mediaId) return;
      let res = await Room.Media.createRoomMediaDownloadUrl(this.roomMedia);
      this.currentPlayer.src = res.data
      this.currentMediaId = this.roomMedia.mediaId
    },
    changeMediaPlayState() {
      if (!this.hasEmceeRole()) {
        this.$alert(this.$t('media.list.noRole'));
        return;
      }

      let play = this.roomMedia.playState === 'play'

      if(this.currentPlayer == undefined) {
        this.reloadCurrentPlayer()
      }

      let ct = Math.floor(this.currentPlayer.currentTime)
      let data = {
        roomGroupId: this.roomMedia.roomGroupId,
        mediaId: this.roomMedia.mediaId,
        seekTime: ct
      }
      play ? this.pauseRoomMedia(data) : this.playRoomMedia(data);
    },
    async play(seekTime) {
      if(this.currentPlayer == undefined) {
        this.reloadCurrentPlayer()
      }
      if(!this.currentPlayer.src || this.currentMediaId != this.roomMedia.mediaId) {
        await this.setRoomMediaDownloadUrl();
      }
      let duration = this.currentPlayer.duration || this.getMediaFile(this.roomMedia).duration;
      this.currentPlayer.pause()
      if(seekTime != undefined) {
        this.currentPlayer.currentTime = seekTime % duration;
      }
      this.notCurrentPlayer.pause();
      this.currentPlayer.play()
          .catch(e => {
            console.error('media play ',e);
            let exceptionType = e && e.name

            // AbortError 무시
            if (exceptionType === 'AbortError') {
              return
            }

            if (exceptionType === 'NotAllowedError') {
              this.currentPlayer.muted = true;
              this.currentPlayer.play()
              if(this.showAlert) {
                this.showAlert = false
                this.$alert({
                  text: this.$t('media.play.disableAutoplay'),
                  onlyOk: true,
                  ok: () => {
                    this.currentPlayer.muted = false;
                    this.currentPlayer.play();
                    this.showAlert = true;
                  },
                })
              }
            }
          })
    },
    pause(seekTime) {
      if(this.currentPlayer == undefined) {
        this.reloadCurrentPlayer()
      }
      if(seekTime != undefined && this.currentPlayer.duration > seekTime) {
        this.currentPlayer.currentTime = seekTime;
      }
      this.notCurrentPlayer.pause();
      this.currentPlayer.pause();
    },
    eventMedia: function(event) {
      let mediaEl = event.target;
      let THIS = this;

      console.log('#### eventMedia', event.type, mediaEl.currentTime, mediaEl.duration);
      switch (event.type) {
        case 'error': {
          console.error('sharemedia play error : readyState[%d], networkState[%d], currentTime[%d]',
              mediaEl.readyState, mediaEl.networkState, mediaEl.currentTime, event.originalEvent);
          break;
        }
        case 'durationchange': {
          let duration = mediaEl.duration;
          let mm = ('' + parseInt(duration / 60)).lpad(2, '0');
          let ss = ('' + parseInt(duration % 60)).lpad(2, '0');

          this.mediaPlayTime = '00:00';
          this.mediaPlayDuration = '/ ' + mm + ':' + ss;
          if(this.roomMedia.playState === 'play') {
            this.play(mediaEl.currentTime)
          }
          break;
        }
        case 'resize': {
          clearTimeout(this.playTimeTimer);
          this.playTimeTimer = setTimeout(function () {
          }, 100);
          break;
        }
        case 'canplay':
          break;
        case 'play':
          if(!mediaEl.paused) {
            this.showMediaControls = true;
          }
          break;
        case 'playing': {
          this.stopTimer = false;
          showPlaytime(mediaEl, this.playTimeTimer);
          // eslint-disable-next-line no-inner-declarations
          function showPlaytime(mediaEl, timer) {
            if (!THIS.stopTimer && !mediaEl.paused) {
              let ct = mediaEl.currentTime + 0.5;
              let mm = ('' + parseInt(ct / 60)).lpad(2, '0');
              let ss = ('' + parseInt(ct % 60)).lpad(2, '0');
              THIS.mediaPlayTime = mm + ':' + ss;
              THIS.mediaTimeRange = parseInt(ct / mediaEl.duration * THIS.rangeMax);

              setTimeout(showPlaytime, (1000 / mediaEl.playbackRate), mediaEl, timer);
            }
            if (mediaEl.src.indexOf('@@none') >= 0) {
              THIS.mediaPlayTime = '';
              THIS.mediaPlayDuration = '';
              THIS.mediaTimeRange = 0;
            }
          }
          break;
        }
        case 'pause': {
          clearTimeout(this.playTimeTimer);
          break;
        }
        case 'seeked': {
          break;
        }
        case 'ended':
          if(this.roomMedia.playType === 'repeat_one') {
            this.roomMedia.playState === 'play' && this.play(0);
          }else {
            let roomUserList = this.roomUserListOrderByEnterDateDesc.slice(0,4);
            let idx = roomUserList.findIndex(roomUser => this.isMe(roomUser))
            if(idx > -1) {
              if(this.roomMedia.playType === 'repeat_all') {
                let roomMediaFile = this.getNextRoomMediaFile(this.roomMedia)
                setTimeout(()=> {
                  THIS.changeRoomMedia({
                    roomGroupId: roomMediaFile.roomGroupId,
                    mediaId: roomMediaFile.mediaId,})}, 1000)

              } else {
                setTimeout(() => {THIS.pauseRoomMedia({
                  roomGroupId: this.roomMedia.roomGroupId,
                  mediaId: this.roomMedia.mediaId,
                  seekTime: 0,
                  autoPause: true
                })}, 1000)
              }
            }
          }

          break;
      }
    },
    toggleVolume() {
      let muted = !this.currentPlayer.muted;
      this.currentPlayer.muted = muted;
      this.notCurrentPlayer.muted = muted;
      this.isMuted = muted;
    },
    changeVolume(e) {
      let ct = e.target.value;
      let muted = ct == 0;
      this.currentPlayer.muted = muted;
      this.currentPlayer.volume = ct;
      this.notCurrentPlayer.muted = muted;
      this.notCurrentPlayer.volume = ct;

      this.isMuted = muted;
    },
    changeSeekTime(value) {
      if (!this.hasEmceeRole()) {
        this.$alert(this.$t('media.list.noRole'));
        return;
      }

      let seekTime = this.currentPlayer.duration * value / this.rangeMax
      this.stopTimer = true;
      this.playRoomMedia({
        roomGroupId: this.roomMedia.roomGroupId,
        mediaId: this.roomMedia.mediaId,
        seekTime: Math.floor(seekTime)
      })
    },
    updateTempSeekTime: throttle(function(e) {
      if (!this.isStartChangeMediaTime) {
        return;
      }

      this.tempSeekTime = e.target.value;
    }, 1000 / 60, {
      leading: true,
      trailing: true
    }),
    changeTempSeekTime() {
      if (!this.isStartChangeMediaTime) {
        return;
      }

      this.changeSeekTime(this.tempSeekTime);
      this.isStartChangeMediaTime = false;
    },
    reloadCurrentPlayer() {
      this.reload = !this.reload
    }
  }
}
const _Events = {
  room_media_player_play: function(e) {
    let {roomGroupId, mediaId, seekTime} = e.data;

    console.log('## room_media play ', e)
    if(this.roomMedia.roomGroupId !== roomGroupId) return;
    if(this.roomMedia.mediaId !== mediaId) {
      console.warn('roomMedia mediaId is not the same :', mediaId, this.roomMedia);
      return;
    }

    this.PLAY_ROOM_MEDIA(e.data);
    this.play(seekTime);
  },
  room_media_player_pause: function(e) {
    let {roomGroupId, seekTime} = e.data;
    console.log('## room_media pause ', e)

    if(this.roomMedia.roomGroupId !== roomGroupId) return;

    this.PAUSE_ROOM_MEDIA(e.data);
    this.pause(seekTime);
  }
}
</script>

<style lang="scss" scoped>
.room-media-share-component {}
.room-media-list-component {
  width: 320px;
  position: absolute;
  top: 8px;
  left: 0;
  transform: translateX(-100%);
  z-index: 2;
  transition: 0.4s ease-in-out;
  &.show {
    transform: translateX(0);
    left: 8px;
  }
}
.room-media-share {
  height: 100%;
  position: relative;
  overflow: hidden;
}
.room-media-share-header {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
}
.room-media-share-info {
  width: max-content;
  padding: 10px;
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: 0.1s ease-out;
  &:hover {
    opacity: 1;
    .room-media-share-button {
      &.menu {
        background: $mainColor url('~@/assets/images/menu_white.svg') no-repeat center / 12px auto;
      }
    }
  }
}
.room-media-share-button {
  transition: 0.1s ease-out;
  &.menu {
    width: 30px;
    height: 30px;
    border-radius: 100%;
    background: rgba(255, 255, 255, 0.2) url('~@/assets/images/menu_white.svg') no-repeat center / 12px auto;
  }
}
.room-media-share-title {
  margin-left: 10px;
  font-size: 13px;
  color: #fff;
}
.room-media-share-body {
  height: 100%;
  position: relative;
}
.room-media-share-none-file {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  &::before {
    content: '';
    width: 100%;
    height: 120px;
    background: url('~@/assets/images/media.svg') no-repeat center / auto 100%;
  }
}
.room-media-share-none-file-text {
  &.title {
    margin-top: 36px;
    font-size: 21px;
    font-weight: 300;
    color: rgba(255, 255, 255, 0.8);
  }
  &.description {
    margin-top: 14px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.4);
  }
}
.room-media-share-none-file-label {}
.room-media-share-none-file-button {
  margin-top: 24px;
}
.room-media-share-video-wrapper {
  height: 100%;
}
.room-media-share-video {
  width: 100%;
  height: 100%;
}
.room-media-share-bottom {
  height: 50px;
  background: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.8));
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0.6;
  z-index: 1;
  transition: 0.1s ease-out;
  &:hover {
    opacity: 1;
  }
}
.room-media-share-controls {
  height: 100%;
  padding-right: 10px;
  display: flex;
  align-items: center;
}
.room-media-share-control-button {
  width: 60px;
  height: 100%;
  transition: 0.1s ease-out;
  &.play {
    background: url('~@/assets/images/play_white.svg') no-repeat center / 18px auto;
  }
  &.pause {
    background: url('~@/assets/images/pause_white.svg') no-repeat center / 18px auto;
  }
  &.speaker {
    width: 50px;
    &.on {
      background: url('~@/assets/images/speaker_on_white.svg') no-repeat center / 18px auto;
    }
    &.off {
      background: url('~@/assets/images/speaker_off_white.svg') no-repeat center / 18px auto;
    }
  }
  &:hover {
    background-color: $mainColor !important;
  }
}
.room-media-share-control-range {
  flex: 1;
  &.volume {
    flex: initial;
    width: 50px;
  }
}
.room-media-share-control-time-container {
  margin: 0 6px 0 12px;
  display: flex;
  align-items: center;
}
.room-media-share-control-time {
  font-size: 13px;
  &.current {
    color: #fff;
  }
  &.duration {
    margin-left: 4px;
    color: rgba(255, 255, 255, 0.3);
  }
}
</style>